import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import CreateEventButton from '../components/CreateEventButton';
import { 
  Check, 
  Briefcase, 
  Users, 
  Zap,
  Crown,
  ArrowRight,
  Calendar,
  Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 99,
    description: 'Perfect for small businesses',
    features: [
      '10 shifts per month',
      'Basic support',
      'Supplier Hub access',
      'Standard response time'
    ],
    icon: <Briefcase className="h-6 w-6" />,
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 299,
    description: 'For growing event companies',
    features: [
      '50 shifts per month',
      'Priority support',
      'Full wholesale access',
      'Advanced analytics',
      'Team collaboration'
    ],
    icon: <Zap className="h-6 w-6" />,
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 599,
    description: 'For large-scale operations',
    features: [
      'Unlimited shifts',
      'Dedicated account manager',
      'Custom integrations',
      'White-label options',
      'SLA guarantee',
      'Priority vendor access'
    ],
    icon: <Crown className="h-6 w-6" />,
    popular: false
  }
];

export default function B2BDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/subscriptions/my-subscription`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: null })),
        axios.get(`${API}/staffing/jobs`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: { jobs: [] } }))
      ]);
      setSubscription(subRes.data);
      setShifts(shiftsRes.data?.jobs || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        plan: planId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Show subscription selection if no active subscription
  if (!subscription) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-12" data-testid="b2b-pricing">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              B2B Membership Plans
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Access our Supplier Hub, post staffing shifts, and scale your events business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map(plan => (
              <Card 
                key={plan.id}
                className={`bg-card border-border relative ${
                  plan.popular ? 'border-indigo-500 ring-2 ring-indigo-500/20' : ''
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-foreground">
                    Most Popular
                  </Badge>
                )}
                <CardHeader className="text-center">
                  <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                    plan.popular ? 'bg-indigo-500/20 text-indigo-400' : 'bg-muted text-muted-foreground'
                  }`}>
                    {plan.icon}
                  </div>
                  <CardTitle className="text-foreground">{plan.name}</CardTitle>
                  <CardDescription className="text-muted-foreground">{plan.description}</CardDescription>
                  <div className="pt-4">
                    <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-lime-400 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-indigo-500 hover:bg-indigo-600' 
                        : 'bg-muted hover:bg-accent'
                    }`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {subscribing === plan.id ? 'Processing...' : 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show B2B dashboard if subscription is active
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="b2b-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
              B2B Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your staffing and vendor access
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge className="bg-lime-500/20 text-lime-500 border-lime-500/30 px-4 py-2">
              <Crown className="h-4 w-4 mr-2" />
              {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} Plan
            </Badge>
            <CreateEventButton />
            <Button onClick={() => navigate('/staffing')} data-testid="post-shift-btn">
              <Plus className="h-4 w-4 mr-2" />
              Post Job
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Subscription</p>
                  <p className="text-2xl font-bold text-foreground capitalize">{subscription.plan}</p>
                </div>
                <div className="p-3 rounded-xl bg-indigo-500/10">
                  <Crown className="h-6 w-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Cost</p>
                  <p className="text-2xl font-bold text-foreground">${subscription.price_monthly}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-500/10">
                  <Calendar className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-2xl font-bold text-green-500 capitalize">{subscription.status}</p>
                </div>
                <div className="p-3 rounded-xl bg-lime-500/10">
                  <Check className="h-6 w-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Eventism Staffing</CardTitle>
              <CardDescription>
                Post FOH & BOH jobs and find qualified staff for your events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/staffing')} className="w-full bg-indigo-500 hover:bg-indigo-600">
                Go to Staffing Marketplace
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Browse Vendors</CardTitle>
              <CardDescription>
                Access wholesale vendors and negotiate B2B rates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/marketplace')} variant="outline" className="w-full">
                Browse Marketplace
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
